<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useContactTimelineStore } from 'dashboard/stores/contactTimeline';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactTimelineMessageList from './ContactTimelineMessageList.vue';
import ContactTimelineDialog from './ContactTimelineDialog.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const PREVIEW_LIMIT = 5;

const { t } = useI18n();
const store = useContactTimelineStore();
const timelineDialogRef = ref(null);

// Store keeps newest-first (natural pagination-append order); the timeline
// itself reads top (oldest) to bottom (newest), like a single chat thread.
const messages = computed(() =>
  [...store.getMessages(props.contactId)].reverse()
);
// Accordion is a preview only — the most recent messages, oldest of the
// bunch first. Full history (with pagination) lives in the dialog.
const previewMessages = computed(() => messages.value.slice(-PREVIEW_LIMIT));
const hasError = computed(() => store.getHasError(props.contactId));
const isFetching = computed(() => store.uiFlags.isFetching);

const retry = () => store.fetchInitial(props.contactId);
const openTimelineDialog = () => timelineDialogRef.value?.open();

const loadIfNotAlreadyLoaded = contactId => {
  if (!store.getHasLoaded(contactId)) {
    store.fetchInitial(contactId);
  }
};

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      loadIfNotAlreadyLoaded(newId);
    }
  }
);

onMounted(() => {
  loadIfNotAlreadyLoaded(props.contactId);
});
</script>

<template>
  <div v-if="isFetching" class="flex items-center justify-center py-5">
    <Spinner />
  </div>
  <div v-else-if="hasError" class="px-4 p-3">
    <p class="text-n-slate-11 mb-2">
      {{ $t('CONTACT_PANEL.TIMELINE.ERROR') }}
    </p>
    <Button
      variant="outline"
      size="sm"
      :label="$t('CONTACT_PANEL.TIMELINE.RETRY')"
      @click="retry"
    />
  </div>
  <div v-else>
    <div v-if="!messages.length" class="no-timeline-message px-4 p-3">
      <span>{{ $t('CONTACT_PANEL.TIMELINE.NO_RECORDS_FOUND') }}</span>
    </div>
    <template v-else>
      <ContactTimelineMessageList :messages="previewMessages" />
      <div class="flex justify-center py-2">
        <Button
          variant="ghost"
          size="sm"
          :label="t('CONTACT_PANEL.TIMELINE.VIEW_ALL')"
          @click="openTimelineDialog"
        />
      </div>
    </template>
  </div>
  <ContactTimelineDialog ref="timelineDialogRef" :contact-id="contactId" />
</template>

<style lang="scss" scoped>
.no-timeline-message {
  @apply text-n-slate-11 mb-4;
}
</style>
